import { FC, useEffect, useState } from "react";

import { Box, Collapse, Stack, useTheme } from "@mui/material";
import { SUGGESTED_AMOUNTS_COUNT_OPTIONS, SuggestedAmountsCount } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { DONATION_FREQUENCIES, getFrequencyConfig } from "../helpers/donationFrequencies";
import { DefaultFrequencyValue, StepSuggestedDonationAmountsFormData } from "../types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardSwitch } from "@/dashboard-design-system/Switch/DashboardSwitch";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { ChevronDown } from "@/icons/outlined";

interface DefaultOptionsBlockProps {
  form: UseFormReturn<StepSuggestedDonationAmountsFormData>;
  onPreselectThirdAmountChange: (checked: boolean) => void;
  onSuggestedAmountsCountChange: (suggestedAmountsCount: SuggestedAmountsCount) => void;
  defaultExpanded?: boolean;
}

export const DefaultOptionsBlock: FC<DefaultOptionsBlockProps> = ({
  form,
  onPreselectThirdAmountChange,
  onSuggestedAmountsCountChange,
  defaultExpanded = false,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const frequencySwitches = form.watch(DONATION_FREQUENCIES.map((frequency) => frequency.switchName));
  const enabledFrequencyOptions = DONATION_FREQUENCIES.filter((_, index) => frequencySwitches[index]).map(
    (frequency) => frequency.interval as DefaultFrequencyValue
  );
  const enabledFrequencyOptionsKey = enabledFrequencyOptions.join(",");

  const defaultAmountsFrequency = form.watch("defaultAmountsFrequency");
  const hasSuggestedAmounts = form.watch("suggestedAmountsCount") > 0;

  useEffect(() => {
    const enabledOptions = enabledFrequencyOptionsKey ? enabledFrequencyOptionsKey.split(",") : [];
    if (enabledOptions.length > 0 && !enabledOptions.includes(defaultAmountsFrequency)) {
      const next = enabledOptions[0] as DefaultFrequencyValue;
      form.setValue("defaultAmountsFrequency", next, { shouldDirty: true });
      if (next !== "oneTime") {
        form.setValue("promoteMonthlyGiving", false, { shouldDirty: true });
      }
    }
  }, [enabledFrequencyOptionsKey, defaultAmountsFrequency, form]);

  return (
    <Stack spacing={0}>
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: 0,
          border: "none",
          background: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
        aria-expanded={expanded}
        aria-controls="default-options-content"
        data-test="default-options-toggle"
      >
        <DashboardTypography variant="subtitle1" vibe="text-neutral-intense" fontWeight="bold">
          {t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.defaultOptionsTitle")}
        </DashboardTypography>
        <DashboardIcon
          size="small"
          vibe="text-neutral-intense"
          sx={{
            display: "flex",
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", { duration: theme.transitions.duration.short }),
          }}
        >
          <ChevronDown />
        </DashboardIcon>
      </Box>
      <Collapse in={expanded} timeout="auto" id="default-options-content">
        <Stack spacing={1} sx={{ marginTop: 1.5 }}>
          <Box
            sx={{
              paddingX: 2,
              paddingY: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.border.neutral.quiet}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent="space-between"
            >
              <Box>
                <DashboardTypography variant="body1" vibe="text-neutral-moderate">
                  {t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.defaultAmountsFrequency")}
                </DashboardTypography>
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <DashboardSelect
                  control={form.control}
                  name="defaultAmountsFrequency"
                  size="small"
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  disabled={enabledFrequencyOptions.length === 0}
                  onChange={(event) => {
                    const value = event.target.value as DefaultFrequencyValue;
                    if (value) {
                      logUserActionEvent(AmplitudeEvents.DefaultFrequencyEdited, { frequency_type: value });
                    }
                    if (value && value !== "oneTime") {
                      form.setValue("promoteMonthlyGiving", false, { shouldDirty: true });
                    }
                  }}
                >
                  {enabledFrequencyOptions.map((value) => (
                    <DashboardMenuItem key={value} value={value}>
                      {t("dashboard", getFrequencyConfig(value).labelKey)}
                    </DashboardMenuItem>
                  ))}
                </DashboardSelect>
              </Box>
            </Stack>
          </Box>
          <Box
            sx={{
              paddingX: 2,
              paddingY: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.border.neutral.quiet}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent="space-between"
            >
              <DashboardTypography variant="body1" vibe="text-neutral-moderate">
                {t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.numberOfSuggestedAmounts")}
              </DashboardTypography>
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <DashboardSelect
                  control={form.control}
                  name="suggestedAmountsCount"
                  size="small"
                  sx={{ width: { xs: "100%", sm: 80 } }}
                  data-test="suggested-amounts-count-select"
                  onChange={(event) => {
                    const suggestedAmountsCount = Number(event.target.value) as SuggestedAmountsCount;
                    logUserActionEvent(AmplitudeEvents.SuggestedAmountsCountEdited, {
                      suggested_amounts_count: suggestedAmountsCount,
                    });
                    onSuggestedAmountsCountChange(suggestedAmountsCount);
                  }}
                >
                  {SUGGESTED_AMOUNTS_COUNT_OPTIONS.map((count) => (
                    <DashboardMenuItem key={count} value={count} data-test={`suggested-amounts-count-option-${count}`}>
                      {count}
                    </DashboardMenuItem>
                  ))}
                </DashboardSelect>
              </Box>
            </Stack>
          </Box>
          <Collapse in={hasSuggestedAmounts} timeout="auto" unmountOnExit>
            <Box
              sx={{
                paddingX: 2,
                paddingY: 1.5,
                borderRadius: 1,
                border: `1px solid ${theme.palette.border.neutral.quiet}`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                useFlexGap
                justifyContent="space-between"
                sx={{ minHeight: 30 }}
              >
                <DashboardTypography variant="body1" vibe="text-neutral-moderate">
                  {t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.preselectDefaultAmount")}
                  <DashboardInformationTooltip
                    alignOnText
                    title={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.preselectDefaultAmount.tooltip")}
                    slotProps={{ icon: { size: "small" } }}
                    sx={{ marginLeft: 1 }}
                  />
                </DashboardTypography>
                <DashboardSwitch
                  checked={form.watch("preselectThirdAmount")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onPreselectThirdAmountChange(event.target.checked);
                  }}
                />
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </Collapse>
    </Stack>
  );
};
